<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import CanalProAPI from 'dashboard/api/canalpro';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import CanalProConfirmDialog from '../components/CanalProConfirmDialog.vue';
import CanalProPageState from '../components/CanalProPageState.vue';
import {
  collectionFromResponse,
  errorMessage,
  formatDateTime,
  recordFromResponse,
} from '../utils/canalproData';

const credentials = ref([]);
const loading = ref(true);
const error = ref('');
const saving = ref(false);
const removing = ref(false);
const validatingId = ref(null);
const changingStatusId = ref(null);
const editingCredential = ref(null);
const removalTarget = ref(null);
const showForm = ref(false);
const searchQuery = ref('');
const statusFilter = ref('');

const form = reactive({
  name: '',
  email: '',
  password: '',
  deviceId: '',
  active: true,
});

const filteredCredentials = computed(() => {
  const term = searchQuery.value.trim().toLocaleLowerCase('pt-BR');

  return credentials.value.filter(credential => {
    const matchesSearch =
      !term ||
      [credential.name, credential.email]
        .filter(Boolean)
        .some(value => String(value).toLocaleLowerCase('pt-BR').includes(term));
    const matchesStatus =
      !statusFilter.value ||
      (statusFilter.value === 'ready' &&
        credential.active !== false &&
        !credential.last_error) ||
      (statusFilter.value === 'attention' && Boolean(credential.last_error)) ||
      (statusFilter.value === 'paused' && credential.active === false);

    return matchesSearch && matchesStatus;
  });
});
const activeCredentialsCount = computed(
  () =>
    credentials.value.filter(credential => credential.active !== false).length
);
const credentialsWithErrorCount = computed(
  () => credentials.value.filter(credential => credential.last_error).length
);
const linkedAccountsCount = computed(() =>
  credentials.value.reduce(
    (total, credential) =>
      total +
      Number(credential.accounts_count || credential.accountsCount || 0),
    0
  )
);
const hasFilters = computed(() =>
  Boolean(searchQuery.value || statusFilter.value)
);

const resetForm = credential => {
  editingCredential.value = credential || null;
  form.name = credential?.name || credential?.label || '';
  form.email = credential?.email || '';
  form.password = '';
  form.deviceId = credential?.device_id || credential?.deviceId || '';
  form.active = credential?.active !== false;
  showForm.value = true;
};

const updateCredentialInList = updated => {
  const position = credentials.value.findIndex(item => item.id === updated.id);
  if (position >= 0) credentials.value.splice(position, 1, updated);
  else credentials.value.unshift(updated);
};

const loadCredentials = async () => {
  loading.value = true;
  error.value = '';
  try {
    credentials.value = collectionFromResponse(await CanalProAPI.credentials());
  } catch (requestError) {
    error.value = errorMessage(
      requestError,
      'Não foi possível carregar os acessos do CanalPro.'
    );
  } finally {
    loading.value = false;
  }
};

const saveCredential = async () => {
  if (!form.name.trim() || !form.email.trim()) {
    useAlert('Informe um nome e o e-mail usado no CanalPro.');
    return;
  }
  if (!editingCredential.value && !form.password) {
    useAlert('Informe a senha usada no CanalPro.');
    return;
  }

  saving.value = true;
  try {
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      device_id: form.deviceId.trim(),
      active: form.active,
      ...(form.password ? { password: form.password } : {}),
    };
    const response = editingCredential.value
      ? await CanalProAPI.updateCredential(editingCredential.value.id, payload)
      : await CanalProAPI.createCredential(payload);
    updateCredentialInList(recordFromResponse(response));
    showForm.value = false;
    useAlert(
      'Acesso salvo. Você já pode verificá-lo para atualizar as contas.'
    );
  } catch (requestError) {
    useAlert(
      errorMessage(requestError, 'Não foi possível salvar este acesso.')
    );
  } finally {
    saving.value = false;
  }
};

const validateCredential = async credential => {
  validatingId.value = credential.id;
  try {
    const response = await CanalProAPI.validateCredential(credential.id);
    updateCredentialInList(recordFromResponse(response));
    useAlert('Acesso confirmado e contas vinculadas atualizadas.');
  } catch (requestError) {
    useAlert(
      errorMessage(
        requestError,
        'Não foi possível confirmar este acesso. Confira os dados e tente novamente.'
      )
    );
  } finally {
    validatingId.value = null;
  }
};

const toggleCredential = async credential => {
  changingStatusId.value = credential.id;
  try {
    const response = await CanalProAPI.updateCredential(credential.id, {
      active: credential.active === false,
    });
    const updated = recordFromResponse(response);
    updateCredentialInList(updated);
    useAlert(
      updated.active === false ? 'Acesso pausado.' : 'Acesso reativado.'
    );
  } catch (requestError) {
    useAlert(
      errorMessage(requestError, 'Não foi possível alterar este acesso.')
    );
  } finally {
    changingStatusId.value = null;
  }
};

const removeCredential = async () => {
  const credential = removalTarget.value;
  if (!credential) return;

  removing.value = true;
  try {
    await CanalProAPI.deleteCredential(credential.id);
    credentials.value = credentials.value.filter(
      item => item.id !== credential.id
    );
    useAlert('Acesso removido.');
    removalTarget.value = null;
  } catch (requestError) {
    useAlert(
      errorMessage(
        requestError,
        'Não foi possível remover este acesso. Pause-o se ele ainda possuir contas vinculadas.'
      )
    );
  } finally {
    removing.value = false;
  }
};

const clearFilters = () => {
  searchQuery.value = '';
  statusFilter.value = '';
};

onMounted(loadCredentials);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    class="h-full min-h-0 overflow-y-auto overscroll-contain bg-slate-50 px-4 py-6 sm:px-6 lg:px-10"
  >
    <section class="mx-auto max-w-7xl">
      <header
        class="overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-indigo-950 to-violet-950 px-5 py-6 text-white shadow-sm sm:px-7"
      >
        <div
          class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p class="text-sm font-medium text-indigo-200">
              CanalPro · Gandalf
            </p>
            <h1 class="mt-1 text-2xl font-semibold tracking-tight">
              Acessos da imobiliária
            </h1>
            <p class="mt-2 max-w-2xl text-sm text-slate-300">
              A senha fica protegida e nunca aparece para a equipe. Verifique os
              acessos para trazer ou atualizar contas.
            </p>
          </div>
          <button
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-indigo-50"
            type="button"
            @click="resetForm()"
          >
            <span class="i-lucide-plus text-base" /> Adicionar acesso
          </button>
        </div>
        <dl class="mt-6 grid grid-cols-3 gap-3 sm:max-w-2xl">
          <div class="rounded-xl bg-white/8 px-3 py-3">
            <dt class="text-xs text-slate-300">Acessos ativos</dt>
            <dd class="mt-1 text-xl font-semibold">
              {{ activeCredentialsCount }}
            </dd>
          </div>
          <div class="rounded-xl bg-white/8 px-3 py-3">
            <dt class="text-xs text-slate-300">Contas vinculadas</dt>
            <dd class="mt-1 text-xl font-semibold">
              {{ linkedAccountsCount }}
            </dd>
          </div>
          <div class="rounded-xl bg-white/8 px-3 py-3">
            <dt class="text-xs text-slate-300">Precisam de atenção</dt>
            <dd class="mt-1 text-xl font-semibold">
              {{ credentialsWithErrorCount }}
            </dd>
          </div>
        </dl>
      </header>

      <CanalProPageState
        v-if="loading || error || !credentials.length"
        class="mt-6"
        :loading="loading"
        :error="error"
        :empty="!loading && !error && !credentials.length"
        empty-title="Nenhum acesso cadastrado"
        empty-description="Adicione o acesso recebido do CanalPro para localizar as contas de anúncios da imobiliária."
        @retry="loadCredentials"
      >
        <template #action>
          <button
            class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            type="button"
            @click="resetForm()"
          >
            Adicionar acesso
          </button>
        </template>
      </CanalProPageState>

      <template v-else>
        <div
          class="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
        >
          <div
            class="grid gap-4 sm:grid-cols-[minmax(16rem,1fr)_13rem_auto] sm:items-end"
          >
            <label class="grid gap-1.5 text-sm font-medium text-slate-700"
              >Buscar acesso
              <div class="relative">
                <span
                  class="i-lucide-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                /><input
                  v-model="searchQuery"
                  class="w-full rounded-lg border-slate-300 py-2 pl-9 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Nome ou e-mail"
                /></div
            ></label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700"
              >Situação<select
                v-model="statusFilter"
                class="rounded-lg border-slate-300 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Todas</option>
                <option value="ready">Prontos para usar</option>
                <option value="attention">Com erro</option>
                <option value="paused">Pausados</option>
              </select></label
            >
            <button
              class="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              type="button"
              :disabled="!hasFilters"
              @click="clearFilters"
            >
              Limpar
            </button>
          </div>
          <p class="mt-3 text-sm text-slate-500">
            {{ filteredCredentials.length }} de {{ credentials.length }} acessos
          </p>
        </div>

        <CanalProPageState
          v-if="!filteredCredentials.length"
          class="mt-6"
          empty
          empty-title="Nenhum acesso corresponde aos filtros"
          empty-description="Ajuste a busca ou limpe os filtros para visualizar os outros acessos."
        >
          <template #action>
            <button
              class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              type="button"
              @click="clearFilters"
            >
              Limpar filtros
            </button>
          </template>
        </CanalProPageState>

        <div v-else class="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="credential in filteredCredentials"
            :key="credential.id"
            class="flex min-h-60 flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <h2 class="truncate font-semibold text-slate-900">
                  {{ credential.name }}
                </h2>
                <p class="mt-1 truncate text-sm text-slate-500">
                  {{ credential.email }}
                </p>
                <p class="mt-3 text-xs text-slate-500">
                  {{
                    credential.accounts_count || credential.accountsCount || 0
                  }}
                  contas encontradas
                </p>
                <p
                  v-if="credential.last_error"
                  class="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700"
                >
                  {{ credential.last_error }}
                </p>
              </div>
              <span
                class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
                :class="
                  credential.active === false
                    ? 'bg-slate-100 text-slate-600'
                    : credential.last_error
                      ? 'bg-rose-50 text-rose-700'
                      : 'bg-emerald-50 text-emerald-700'
                "
              >
                {{
                  credential.active === false
                    ? 'Pausado'
                    : credential.last_error
                      ? 'Precisa de atenção'
                      : 'Pronto para usar'
                }}
              </span>
            </div>

            <p
              v-if="!credential.last_error"
              class="mt-4 text-xs text-slate-500"
            >
              <span
                class="i-lucide-shield-check mr-1 inline-block align-text-bottom"
              />
              {{
                credential.last_validated_at
                  ? `Verificado em ${formatDateTime(credential.last_validated_at)}`
                  : 'Ainda não verificado'
              }}
            </p>

            <div
              class="mt-auto flex flex-wrap gap-2 border-t border-slate-100 pt-4"
            >
              <button
                class="rounded-md bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-wait disabled:opacity-60"
                type="button"
                :disabled="
                  validatingId === credential.id || credential.active === false
                "
                @click="validateCredential(credential)"
              >
                {{
                  validatingId === credential.id
                    ? 'Verificando…'
                    : 'Verificar acesso'
                }}
              </button>
              <button
                class="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                type="button"
                @click="resetForm(credential)"
              >
                Editar
              </button>
              <button
                class="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                type="button"
                :disabled="changingStatusId === credential.id"
                @click="toggleCredential(credential)"
              >
                {{ credential.active === false ? 'Reativar' : 'Pausar' }}
              </button>
              <button
                class="rounded-md px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                type="button"
                @click="removalTarget = credential"
              >
                Remover
              </button>
            </div>
          </article>
        </div>
      </template>
    </section>

    <LaisModal
      :show="showForm"
      :title="editingCredential ? 'Editar acesso' : 'Adicionar acesso'"
      max-width-class="max-w-lg"
      @close="showForm = false"
    >
      <form class="grid gap-4" @submit.prevent="saveCredential">
        <p class="text-sm text-slate-500">
          Use os dados recebidos ao contratar o CanalPro.
        </p>
        <label class="grid gap-1.5 text-sm font-medium text-slate-700">
          Como você quer chamar este acesso?
          <input
            v-model="form.name"
            class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
            maxlength="80"
            placeholder="Ex.: Matriz São Paulo"
            autocomplete="organization"
          />
        </label>
        <label class="grid gap-1.5 text-sm font-medium text-slate-700">
          E-mail do CanalPro
          <input
            v-model="form.email"
            class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
            type="email"
            placeholder="nome@imobiliaria.com.br"
            autocomplete="username"
          />
        </label>
        <label class="grid gap-1.5 text-sm font-medium text-slate-700">
          {{
            editingCredential
              ? 'Nova senha (deixe em branco para manter)'
              : 'Senha do CanalPro'
          }}
          <input
            v-model="form.password"
            class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
            type="password"
            autocomplete="current-password"
          />
        </label>
        <label class="grid gap-1.5 text-sm font-medium text-slate-700">
          Identificação deste computador
          <input
            v-model="form.deviceId"
            class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="Preencha somente se o CanalPro forneceu este dado"
          />
          <span class="text-xs font-normal text-slate-500"
            >Na maioria dos casos, este campo pode ficar em branco.</span
          >
        </label>
        <label
          class="flex items-center gap-3 rounded-lg bg-slate-50 p-4 text-sm text-slate-700"
        >
          <input
            v-model="form.active"
            class="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            type="checkbox"
          />
          Deixar este acesso disponível para uso
        </label>
      </form>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            class="rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            type="button"
            @click="showForm = false"
          >
            Cancelar
          </button>
          <button
            class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            type="button"
            :disabled="saving"
            @click="saveCredential"
          >
            {{ saving ? 'Salvando…' : 'Salvar acesso' }}
          </button>
        </div>
      </template>
    </LaisModal>

    <CanalProConfirmDialog
      :open="Boolean(removalTarget)"
      title="Remover este acesso?"
      description="A remoção só é permitida quando não existem contas vinculadas. Se você pretende voltar a usar o acesso, prefira pausá-lo."
      confirm-label="Remover acesso"
      variant="danger"
      :loading="removing"
      @cancel="removalTarget = null"
      @confirm="removeCredential"
    />
  </main>
</template>
